import { ee } from '../../services/earth-engine';
import { mergeProperties, retrieveExtremes, getDate } from '../utils';
import { scoreCloudRatio } from '../imagery';
import * as Metadata from '../../common/metadata';

/**
 * @function getMedianImageLandsat
 * @description
 * get median image through Landsat sattelite
 * 1. Get collection filtering ROI and date
 * 2. Calculates NDWI (Normalized Difference Water Index)
 * 3. Mask areas to keep only pixels where NDWI ≥ 0 (water areas)
 * 4. Reduce collection to median to remove clouds and temporal anomalies
 * 5. Generate thumbnail
 * 6. Rename median bands (remove "_median" suffix)
 * 7. Rounds the median values (px) and converts them back to integers
 * 8. Return thumbnail URL and processed image
 * @param {String} missionName - sattelite mission name
 * @param {ee.Geometry} geometry - ROI
 * @param {String} dtBegin - date to begin image search
 * @param {String} dtEnd - date to end image search
 * @returns {Array} median image url and median image
 */
const getMedianImageLandsat = (missionName, geometry, dtBegin, dtEnd) => {
  const NDWIindex = (image) => {
    const ndwi = image.normalizedDifference(['SR_B3', 'SR_B5']).rename('ndwi'); //Mc Feeters, 1996
    return image.addBands([ndwi]);
  };
  const mask_land = (image) => {
    const ndwi = image.select('ndwi');
    return image.updateMask(ndwi.gte(0));
  };
  let finalCollection = ee.ImageCollection(missionName).filterBounds(geometry).filterDate(dtBegin, dtEnd);
  finalCollection = finalCollection.map(NDWIindex);
  finalCollection = finalCollection.map(mask_land);
  let medianImage = finalCollection.reduce(ee.Reducer.median());
  medianImage = medianImage.clip(geometry);
  const medianThumbURL = medianImage.getThumbURL({
    region: geometry,
    bands: ['SR_B4_median', 'SR_B3_median', 'SR_B2_median'],
    dimensions: 512,
    max: 20000,
    min: 7000,
    format: 'jpg',
  });
  medianImage = medianImage
    .select([
      'SR_B1_median',
      'SR_B2_median',
      'SR_B3_median',
      'SR_B4_median',
      'SR_B5_median',
      'SR_B6_median',
      'SR_B7_median',
      'SR_QA_AEROSOL_median',
      'QA_PIXEL_median',
    ])
    .rename(['SR_B1', 'SR_B2', 'SR_B3', 'SR_B4', 'SR_B5', 'SR_B6', 'SR_B7', 'SR_QA_AEROSOL', 'QA_PIXEL']);
  medianImage = medianImage.toInt();
  return [medianThumbURL, medianImage];
};

/**
 * @function calculateCloudCoverage
 * @description Calculates cloud percentage in each image for a given region of interest (ROI).
 * Uses the QA_PIXEL band from Landsat imagery to detect clouds based on bit flags and confidence levels.
 * Only calculates cloud coverage over valid data areas (where the satellite has coverage).
 * @param {Object} satellite - Satellite object containing missions array with collection names
 * @param {ee.Geometry} geometry - Region of interest (ROI)
 * @param {String} startDate - - Start date for filtering images (format: "YYYY-MM-DD")
 * @see{@link https://eol.pages.cms.hu-berlin.de/gcg_eo/02_data_quality.html} - Landsat data quality - Humboldt-Universität zu Berlin - Geography Department - Earth Observation Lab
 * @returns {ee.ImageCollection} Collection with added 'roiCloudPercentage' property (0-1) for each image
 * @throws {Error} Whatever went wrong, unchanged. It used to answer a failure with `ee.Number(0.01)`
 *  instead - a number where every caller expects a collection, so the next line to touch it died on
 *  `.filter is not a function` or `.features of undefined`, three frames from the real problem and
 *  carrying none of it.
 * @example
 * Cloud confidence threshold levels (bits 8-9 in QA_PIXEL):
 * 00 = Not determined (0)
 * 01 = Low (1)
 * 10 = Medium (2)
 * 11 = High (3)
 * Bitwise operation example:
 * QA_PIXEL value:  01011010
 * cloudBitMask:    00001000 (bit 3)
 * AND result:      00001000 (≠ 0, cloud exists)
 */
export const calculateCloudCoverage = function (satellite, geometry, startDate, collection = ee.ImageCollection([])) {
  const endDate = ee.Date(Date.now());
  const roi = geometry;
  const cloudConfidenceThresh = 2;
  try {
    if (collection.size().getInfo() === 0) {
      const landsatLT05TOACollection = ee
        .ImageCollection(satellite.missions[0].name)
        .filterBounds(roi)
        .filterDate(startDate, endDate);
      const landsatLC08TOACollection = ee
        .ImageCollection(satellite.missions[1].name)
        .filterBounds(roi)
        .filterDate(startDate, endDate);
      const landsatLC09TOACollection = ee
        .ImageCollection(satellite.missions[2].name)
        .filterBounds(roi)
        .filterDate(startDate, endDate);
      collection = landsatLT05TOACollection.merge(landsatLC08TOACollection).merge(landsatLC09TOACollection);
    }
    const roiCloudCollection = collection.map((image) => {
      const clippedImage = image.clip(roi);
      const qa = clippedImage.select('QA_PIXEL');
      const validDataMask = qa.mask();
      const validAreaImage = validDataMask.multiply(ee.Image.pixelArea());
      const validArea = validAreaImage
        .reduceRegion({
          reducer: ee.Reducer.sum(),
          geometry: roi,
          scale: 30,
          maxPixels: 1e9,
        })
        .get('QA_PIXEL');
      const cloudBitMask = 1 << 3;
      const cloudMask = qa.bitwiseAnd(cloudBitMask).neq(0);
      const cloudConfidenceBitMask = 3 << 8;
      const cloudConfidence = qa.bitwiseAnd(cloudConfidenceBitMask).rightShift(8);
      const likelyCloudMask = cloudMask.or(cloudConfidence.gte(cloudConfidenceThresh)).rename('CLOUDS');
      const likelyCloudMaskArea = likelyCloudMask.multiply(ee.Image.pixelArea());
      const sumCloudValuesInCloudArea = likelyCloudMaskArea
        .reduceRegion({
          reducer: ee.Reducer.sum(),
          geometry: roi,
          scale: 30,
          maxPixels: 1e9,
        })
        .get('CLOUDS');
      const calculatedCloudPercentage = ee.Number(sumCloudValuesInCloudArea).divide(ee.Number(validArea));
      return image.set({ roiCloudPercentage: calculatedCloudPercentage });
    });
    return roiCloudCollection;
  } catch (e) {
    console.error('calculateCloudCoverage error:', e);
    throw e;
  }
};

/**
 * @function sliceByRevisit
 * @description Filters an image collection by a time period, either by minutes (for exact timestamps) or by days (from start of day).
 * @param {ee.ImageCollection} collection - Earth Engine image collection to filter
 * @param {String|ee.Date} startingDate - Starting date for the filter (ISO string or ee.Date)
 * @param {number} [days=0] - Number of days to advance from starting date (used when minutes is 0)
 * @param {number} [minutes=0] - Number of minutes to advance from starting date (overrides days if > 0)
 * * Called By:
 * @see {acquireFromDate} Image Filter step - gets collection and apply mosaic
 * @returns {ee.ImageCollection} Filtered image collection within the specified time range
 */
const sliceByRevisit = (collection, startingDate, days = 0, minutes = 0) => {
  const start = ee.Date(startingDate);
  if (minutes > 0) {
    return collection.filterDate(start.advance(-1, 'minute'), start.advance(minutes, 'minute'));
  }
  const startOfDay = start.update(null, null, null, 0, 0, 0);
  return collection.filterDate(startOfDay, startOfDay.advance(days, 'day'));
};

export const maskTOAClouds = (image, bandName) => {
  const band = image.select(bandName);
  const cloudMask = 1 << 4,
    cloudConf = 3 << 5,
    shadowConf = 3 << 7,
    cirrusConf = 3 << 11;
  return band
    .bitwiseAnd(cloudMask)
    .and(band.bitwiseAnd(cloudConf).gt(1).or(band.bitwiseAnd(shadowConf).gt(1)).or(band.bitwiseAnd(cirrusConf).gt(1)));
};

export const acquireFromDate = (date, mission, geometry, isOneExactDate = false) => {
  const slice = sliceByRevisit(
    ee.ImageCollection(mission.name).filterBounds(geometry),
    date,
    isOneExactDate ? 0 : mission.cycle,
    isOneExactDate ? 1 : 0,
  );
  const mosaicked = slice.mosaic().clip(geometry);
  const image = mosaicked.set(mergeProperties(slice));
  const ratio = scoreCloudRatio(maskTOAClouds(image, mission.bands.qa), mission.bands.qa, geometry);
  return image.set('CLOUDS', ratio);
};

/**
 * @function processCollection
 * @definition Computes the available dates in the region, each carrying its
 * scene-level cloud percentage, without touching pixel data.
 * 1. Retrieve the extreme dates (earliest and latest) in the (optionally date-scoped) query
 * 2. Generate cycle-length slices (0, cycle, 2*cycle, ...) from the earliest date
 * 3. Transform each slice into an empty image carrying that window's metadata, if any image exists in it
 * 4. Keep only the carriers whose combined geometries contain the AOI
 * @param {Object} mission - sattelite data
 * @param {ee.Geometry.Polygon} geometry - ROI's coordinate
 * @see {@link retrieveExtremes} - Function that gets the earliest and latest images in the collection
 * @returns {ee.ImageCollection} carrier images, one per cycle window that overlaps the AOI
 */
export const processCollection = (mission, geometry, startDate, endDate) => {
  let query = ee.ImageCollection(mission.name).filterBounds(geometry);
  if (startDate || endDate) {
    query = query.filterDate(startDate || `${mission.startYear}-01-01`, endDate || ee.Date(Date.now()));
  }
  const process = (available) => {
    const global = retrieveExtremes(available);
    const earliestDate = global.earliest;
    const completeCycles = ee
      .Number(global.latest.difference(earliestDate, 'day').abs())
      .divide(mission.cycle)
      .ceil()
      .max(1);
    const additions = ee.List.sequence(0, null, mission.cycle, completeCycles);
    const carriers = additions.map((increment) => {
      const startingDate = earliestDate.advance(increment, 'day');
      const collection = sliceByRevisit(available, startingDate, mission.cycle);
      const empty = ee.Algorithms.IsEqual(collection.size(), 0);
      const firstImage = ee.Image(collection.sort(Metadata.TIME_START).first());
      const cloudCoverage = ee.Algorithms.If(
        empty,
        0,
        ee.Algorithms.If(firstImage.propertyNames().contains('CLOUD_COVER'), firstImage.get('CLOUD_COVER'), 0),
      );
      const properties = ee.Algorithms.If(empty, {}, mergeProperties(collection));
      return ee.Image(0).set(properties).set('CLOUD_COVER', cloudCoverage);
    });
    return ee.ImageCollection.fromImages(carriers).filter(ee.Filter.contains(Metadata.FOOTPRINT, geometry));
  };

  return ee.ImageCollection(ee.Algorithms.If(query.size().gt(0), process(query), ee.ImageCollection([])));
};

export const generateCloudMap = (collection) => {
  const dates = collection.aggregate_array('system:time_start').map((d) => ee.Date(d).format(null, 'UTC'));
  const clouds = collection.aggregate_array('CLOUD_COVER');
  return ee.Dictionary.fromLists(dates, clouds);
};

const queryAvailable = (mission) => (geometry, startDate, endDate) => {
  const collection = processCollection(mission, geometry, startDate, endDate);
  return generateCloudMap(collection);
};

const getAvailable = (mission) => (geometry) => {};

const acquire = (mission) => (date, geometry) => {
  return acquireFromDate(date, mission, geometry, true);
};

const format = (properties) => {
  return properties['system:time_start'] + ' -- ' + properties['cloud'];
};

export default {
  getMedianImageLandsat,
  queryAvailable,
  getAvailable,
  acquire,
  format,
};
